import React, { useEffect, useState } from "react";
import _ from "lodash";
import { useLocation } from "react-router";
import {
  Table,
  Header,
  HeaderRow,
  Body,
  Row,
  Cell,
} from "@table-library/react-table-library/table";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import GetAppIcon from "@material-ui/icons/GetApp";
import {
  createStyles,
  makeStyles,
  Typography,
  useMediaQuery,
  Grid,
  useTheme,
  FormControlLabel,
  Checkbox,
  TextField,
  Menu,
  MenuItem,
} from "@material-ui/core";
import {
  HeaderCellSort,
  useSort,
} from "@table-library/react-table-library/sort";
import "jspdf-autotable";
import "jspdf-autotable";
import { Icon } from "@iconify/react";

import { CustomTheme } from "../../../../types/customTheme";
import CardCommon from "../../../../components/card/CardCommon";
import LocationDetailsCard from "../../../../components/common/LocationDetailsCard";
import {
  sortDatesTime,
  sortNumbers,
  sortStrings,
} from "../../../../utils/commonArrayMap";
import TextfieldCommon from "../../../../components/textField/TextfieldCommon";
import MenuButtonCommon from "../../../../components/buttons/MenuButtonCommon";
import { handleDownloadPdf } from "./convertPdfExcel/convertDataToPdf";
import { handleDownloadCsv } from "./convertPdfExcel/convertDataToExcel";
import {
  convertDateFormat,
  convertDateTimeFormat,
} from "../../../../utils/ConvertDateTimeFormat";

interface UpdatedNodeItem {
  date: string;
  status: string;
  orderNo?: string;
  total: string;
  fees: string;
  transferred: string;
}

export interface VoidSummeryInfoNodeProps {
  nodeList: any;
  filterDetails: any;
  locationSelectedList: any;
  handleOnClickText: any;
  locationInfoMap: any;
}

const useStyles = (nodesLength: number, bodyLength: number) =>
  makeStyles((theme: CustomTheme) =>
    createStyles({
      startTextStyle: {
        fontWeight: "bold",
        display: "flex",
        justifyContent: "start",
        paddingLeft: "16px",
        fontSize: "12px",
      },
      textStyleHeader: {
        display: "flex",
        justifyContent: "center",
        fontWeight: "bold",
        fontSize: "12px",
      },
      endTextStyle: {
        display: "flex",
        justifyContent: "end",
        alignItems: "center",
        fontSize: "12px",
        paddingRight: "20px",
      },
      endTextStyleIndexFour: {
        display: "flex",
        justifyContent: "end",
        alignItems: "center",
        fontSize: "12px",
      },
      headerEndTextStyle: {
        display: "flex",
        justifyContent: "end",
        fontSize: "12px",
        paddingRight: "8px",
      },
      footerEndTextStyle: {
        display: "flex",
        justifyContent: "end",
        fontSize: "12px",
      },
      footerEndTextStyles: {
        display: "flex",
        justifyContent: "end",
        fontSize: "12px",
        paddingRight: "20px",
      },
      textStyle: {
        display: "flex",
        justifyContent: "center",
        fontSize: "12px",
      },
      headerStyle: {
        "&:last-of-type": {
          backgroundColor: theme.palette.background.table_header_background,
          color: theme.palette.custom.orange.contrastText,
        },
      },
      finalTotalRow: {
        cursor: "pointer",
        height: "40px",
        [`&:nth-of-type(${bodyLength + 1})`]: {
          color: theme.palette.custom["green"].contrastText,
          backgroundColor: theme.palette.custom.green.main,
          fontWeight: "bold",
          "&:hover": {
            backgroundColor: theme.palette.custom.green.light,
          },
        },
      },
      totalRow: {
        cursor: "pointer",
        "&.css-16gtl2w": {
          display: "flex",
          justifyContent: "center",
        },
        "&:nth-of-type(n)": {
          color: theme.palette.custom["green"].contrastText,
          backgroundColor: theme.palette.custom.green.main,
          "&:hover": {
            backgroundColor: theme.palette.custom.green.light,
          },
        },
      },
      headerRow: {
        fontSize: "13px",
        cursor: "pointer",
        "&.css-16gtl2w": {
          display: "flex",
          justifyContent: "center",
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.background.paper,
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        },
        "&:nth-of-type(even)": {
          backgroundColor: theme.palette.background.entity_background,
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        },
      },
      baseCellRow: {
        placeItems: "center",
        "& .css-16gtl2w": {
          fontWeight: "bold",
        },
        height: "40px",
        "&:nth-of-type(1)": {
          left: "0px",
          minWidth: "20%",
          width: "20%",
        },
        [theme.breakpoints.down("xl")]: {
          [`&:nth-of-type(n+2):nth-of-type(-n+${5})`]: {
            minWidth: `${80 / 5}%`,
            width: `${80 / 5}%`,
            "& .css-16gtl2w": {
              display: "flex",
              justifyContent: "center",
              fontWeight: "bold",
            },
          },
          [`&:nth-of-type(${6})`]: {
            minWidth: `${80 / 5}%`,
            width: `${80 / 5}%`,
            "& .css-16gtl2w": {
              display: "flex",
              justifyContent: "end",
              fontWeight: "bold",
              paddingRight: "16px",
            },
          },
        },
        [theme.breakpoints.down("xs")]: {
          [`&:nth-of-type(n+2):nth-of-type(-n+${5})`]: {
            minWidth: "30%",
            width: "30%",
            "& .css-16gtl2w": {
              display: "flex",
              justifyContent: "center",
              fontWeight: "bold",
            },
          },
          [`&:nth-of-type(${6})`]: {
            minWidth: "30%",
            width: "30%",
            "& .css-16gtl2w": {
              display: "flex",
              justifyContent: "end",
              fontWeight: "bold",
              paddingRight: "16px",
            },
          },
          "&:nth-of-type(1)": {
            left: "0px",
            minWidth: "48%",
            width: "48%",
          },
        },
      },
    }),
  );

/**
 * This component represents a tabbed view for displaying daily, weekly, and monthly order reports.
 * It allows users to switch between different time intervals and export the data as PDF or CSV.
 * The component fetches data based on the selected time interval and location, and renders it accordingly.
 */
const DailyOrderReportTab: React.FunctionComponent<
  VoidSummeryInfoNodeProps
> = ({
  nodeList,
  filterDetails,
  locationSelectedList,
  handleOnClickText,
  locationInfoMap,
}) => {
  const [nodes, setNodes] = useState<any>({ nodes: [] });
  const [footer, setFooter] = useState<any>([]);
  const [pdfHeaders, setPdfHeaders] = useState<any>([]);
  const [excelHeaders, setExcelHeaders] = useState<any>([]);
  const [headersList, setHeadersList] = useState<any>([]);
  const [nodesPdfExcel, setNodesPdfExcel] = useState<any>([]);
  const [nodesLength, setNodesLength] = useState(0);
  const [bodyLength, setBodyLength] = useState(0);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSucceeded, setShowSucceeded] = useState(true);
  const [showRefunded, setShowRefunded] = useState(true);

  const { search } = useLocation();
  const params = new URLSearchParams(search);

  useEffect(() => {
    setNodes({ nodes: [] });
  }, [params.get("startDate")]);

  useEffect(() => {
    if (nodeList?.length > 0) {
      const filtered = nodeList
        .filter((node: any) => {
          const status = node.status?.toLowerCase() || "";
          if (
            (status === "succeeded" && !showSucceeded) ||
            (status === "refunded" && !showRefunded)
          ) {
            return false;
          }
          return true;
        })
        .filter((node: any) => {
          if (!searchTerm) return true;
          const str = JSON.stringify(node).toLowerCase();
          return str.includes(searchTerm.toLowerCase());
        })
        .map((node: any) => {
          const date: any = new Date(node.created * 1000);
          const transferredAmount = node.transferredAmount ?? 0;
          const applicationFee = node.applicationFee ?? 0;
          const fees = node.amount - transferredAmount + applicationFee;
          const serviceCharge = locationInfoMap.isDeductServiceCharge
            ? parseFloat(locationInfoMap.serviceCharge)
            : 0;
          return {
            date: convertDateTimeFormat(date),
            status: node.status,
            orderNo: node.metadata?.order_no,
            total: (node.amount - serviceCharge).toFixed(2),
            fees: (fees - serviceCharge).toFixed(2),
            transferred: (transferredAmount - applicationFee).toFixed(2),
            createdRaw: date, // keep a raw date for sorting
          };
        })
        // ✅ Sort ascending by created date
        .sort((a: any, b: any) => a.createdRaw - b.createdRaw);

      // ✅ Calculate totals
      const totalTotal = filtered.reduce(
        (sum: any, item: any) => sum + parseFloat(item.total || "0"),
        0,
      );
      const totalFees = filtered.reduce(
        (sum: any, item: any) => sum + parseFloat(item.fees || "0"),
        0,
      );
      const totalTransferred = filtered.reduce(
        (sum: any, item: any) => sum + parseFloat(item.transferred || "0"),
        0,
      );

      // ✅ Append totals row
      const totalsRow = {
        date: "Total",
        status: "",
        orderNo: "",
        total: totalTotal.toFixed(2),
        fees: totalFees.toFixed(2),
        transferred: totalTransferred.toFixed(2),
      };
      const pdfExcelNodeList = _.cloneDeep(filtered);
      pdfExcelNodeList.push(totalsRow);
      setFooter([totalsRow]);
      setNodes({ nodes: filtered });
      setNodesPdfExcel(pdfExcelNodeList);

      setNodesLength(filtered.length);
      setBodyLength(filtered.length);

      setPdfHeaders([
        { title: "Date", field: "date" },
        { title: "Status", field: "status" },
        { title: "Order No", field: "orderNo" },
        { title: "Total", field: "total" },
        { title: "Fees", field: "fees" },
        { title: "Transferred", field: "transferred" },
      ]);
      setExcelHeaders([
        { header: "Date", key: "date", width: 52, height: 68 },
        { header: "Status", key: "status", width: 32, height: 68 },
        { header: "Order No", key: "orderNo", width: 32, height: 68 },
        { header: "Total", key: "total", width: 32, height: 68 },
        { header: "Fees", key: "fees", width: 32, height: 68 },
        { header: "Transferred", key: "transferred", width: 32, height: 68 },
      ]); // Set table headers
      setHeadersList([
        "Date",
        "Status",
        "Order No",
        "Total",
        "Fees",
        "Transferred",
      ]);
    }
  }, [nodeList, searchTerm, showSucceeded, showRefunded]);

  const maxWidthLocationChips = useMediaQuery("(max-width: 600px)");

  const maxWidth = useMediaQuery("(max-width: 960px)");
  const maxWidthCustom = useMediaQuery("(max-width: 1600px)");

  // Create a pay In Out sorting function
  const sort = useSort(
    nodes,
    {},
    {
      sortFns: {
        Date: (array) => sortDatesTime(array, "date"),
        Status: (array) => sortStrings(array, "status"),
        "Order No": (array) => sortStrings(array, "orderNo"),
        Total: (array) => sortNumbers(array, "total"),
        Fees: (array) => sortNumbers(array, "fees"),
        Transferred: (array) => sortNumbers(array, "transferred"),
      },
      sortIcon: {
        margin: "0px",
        iconDefault: (
          <Icon
            icon="fluent:chevron-up-down-16-filled"
            width="44"
            height="44"
          />
        ),
        iconUp: <ArrowDropDownIcon />,
        iconDown: <ArrowDropUpIcon />,
      },
    },
  );

  /* Open PDF / Excel selection menu */
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /* Close PDF / Excel selection menu */
  const handleClose = () => {
    setAnchorEl(null);
  };

  const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const color =
      status?.toLowerCase() === "succeeded"
        ? theme.palette.custom.green.main // green
        : status?.toLowerCase() === "refunded"
        ? theme.palette.custom.red.main // red
        : "#9E9E9E"; // grey for others

    return (
     <span
      style={{
        backgroundColor: color,
        color: "#fff",
        borderRadius: "12px",
        padding: "2px 8px",
        fontSize: "11px",
        fontWeight: "bold",
        textTransform: "capitalize",
        display: "inline-flex",
        justifyContent: "center",
        alignItems: "center",
        minWidth: "80px", // 👈 ensures both badges are the same width
        textAlign: "center",
      }}
    >
        {status}
      </span>
    );
  };

  const theme: CustomTheme = useTheme();
  const open = Boolean(anchorEl);
  const classes: any = useStyles(nodesLength, bodyLength)();

  return (
    <>
      <Grid
        container
        style={{
          marginBottom: "20px",
          marginTop: "8px",
        }}
      >
        <Grid item xs={12}>
          <div
            style={
              maxWidthLocationChips
                ? { display: "block" }
                : {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }
            }
          >
            {/* <LocationDetailsCard
              locationSelectedList={locationSelectedList}
              handleOnClickText={handleOnClickText}
            /> */}
          </div>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        style={{ marginBottom: "16px", alignItems: "center" }}
      >
        <Grid
          item
          xs={12}
          sm={6}
          style={{ display: "flex", justifyContent: "start" }}
        >
          <TextfieldCommon
            id="sign-email-input"
            name="sign-email-input"
            style={{ width: "300px" }}
            type="text"
            label="Search"
            value={searchTerm}
            onChange={(e: any) => setSearchTerm(e.target.value)}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          style={{ display: "flex", justifyContent: "end" }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={showSucceeded}
                onChange={(e) => setShowSucceeded(e.target.checked)}
                color="secondary"
              />
            }
            label="Succeeded"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showRefunded}
                onChange={(e) => setShowRefunded(e.target.checked)}
                color="secondary"
              />
            }
            label="Refunded"
          />
          {!_.isEmpty(nodes.nodes) && (
            <div
              style={
                maxWidthLocationChips
                  ? {
                      display: "flex",
                      justifyContent: "end",
                      marginTop: "8px",
                    }
                  : { marginLeft: "16px" }
              }
            >
              <MenuButtonCommon
                id="basic-button"
                variant="contained"
                style={{
                  fontSize: 11,
                  height: "38px",
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  width: "160px",
                }}
                color="blue"
                aria-controls={open ? "basic-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                onClick={handleClick}
              >
                <GetAppIcon />
                <Typography
                  variant="body1"
                  style={{ paddingLeft: "16px", color: "white" }}
                >
                  Export
                </Typography>
              </MenuButtonCommon>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  "aria-labelledby": "basic-button",
                }}
                PaperProps={{
                  elevation: 0,
                  style: {
                    borderRadius: "10px",
                    border: `1px solid ${theme.palette.background.entity_border}`,
                  },
                }}
                style={{ top: "56px" }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <MenuItem
                  onClick={() => {
                    handleDownloadPdf(
                      "p",
                      pdfHeaders,
                      nodesPdfExcel,
                      filterDetails,
                    );
                    handleClose();
                  }}
                >
                  Export as PDF
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleDownloadCsv(
                      excelHeaders,
                      nodesPdfExcel,
                      filterDetails,
                      bodyLength,
                    );
                    handleClose();
                  }}
                >
                  Export as CSV
                </MenuItem>
              </Menu>
            </div>
          )}
        </Grid>
      </Grid>

      <CardCommon>
        <Table
          data={nodes}
          sort={sort}
          layout={{ custom: maxWidthCustom, horizontalScroll: maxWidth }}
        >
          {(tableList) => (
            <>
              <Header style={{ zIndex: 0 }}>
                <HeaderRow className={classes.headerStyle}>
                  {headersList.map((data: any, index: any) => (
                    <HeaderCellSort
                      sortKey={data}
                      pinLeft={index === 0}
                      className={classes.baseCellRow}
                      style={{ fontWeight: "bold" }}
                    >
                      <Typography
                        variant="caption"
                        className={
                          index === 0
                            ? classes.startTextStyle
                            : classes.textStyleHeader
                        }
                      >
                        {data}
                      </Typography>
                    </HeaderCellSort>
                  ))}
                </HeaderRow>
              </Header>

              <Body className={classes.bodyStyle}>
                {tableList.map((item, bodyIndex) => (
                  <>
                    <Row
                      key={item.gross20}
                      item={item}
                      className={classes.headerRow}
                    >
                      {Object.keys(item).map((data: any, index: any) => (
                        <>
                          {data !== "createdRaw" && (
                            <Cell
                              pinLeft={index === 0 ? true : false}
                              className={classes.baseCellRow}
                            >
                              <Typography
                                variant="caption"
                                align="left"
                                className={
                                  index === 0
                                    ? classes.startTextStyle
                                    : index === 5
                                    ? classes.endTextStyle
                                    : classes.textStyle
                                }
                              >
                                {data === "status" ? (
                                  <StatusBadge status={item[data]} />
                                ) : (
                                  item[data]
                                )}
                              </Typography>
                            </Cell>
                          )}
                        </>
                      ))}
                    </Row>
                    {bodyIndex + 1 === tableList.length &&
                      footer.map((data1: any) => (
                        <>
                          <Row
                            className={classes.finalTotalRow}
                            key={item.gross20}
                            item={item}
                          >
                            {Object.keys(item).map((data: any, index: any) => (
                              <>
                                {data !== "createdRaw" && (
                                  <Cell
                                    pinLeft={index === 0 ? true : false}
                                    className={classes.baseCellRow}
                                  >
                                    <Typography
                                      variant="caption"
                                      style={{
                                        fontWeight: "bold",
                                        color: "white",
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                      className={
                                        index === 0
                                          ? classes.startTextStyle
                                          : index === 5
                                          ? classes.endTextStyle
                                          : classes.textStyle
                                      }
                                    >
                                      {data1[data]}
                                    </Typography>
                                  </Cell>
                                )}
                              </>
                            ))}
                          </Row>
                        </>
                      ))}
                  </>
                ))}
              </Body>
            </>
          )}
        </Table>
      </CardCommon>
    </>
  );
};

export default DailyOrderReportTab;
